import { jsx as _jsx } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { DEFAULT_PRODUCT_BADGES, } from '../../renderer/context.js';
/** Absolute placement for each corner, with the same 12px inset on both axes. */
function placement(position) {
    const inset = '12px';
    const vertical = position.startsWith('top') ? { top: inset } : { bottom: inset };
    // Logical properties, so an Arabic shop pins "top right" to the reader's right — the mirror of the
    // French shop, which is what "right" means to the person reading it.
    const horizontal = position.endsWith('left')
        ? { insetInlineStart: inset }
        : { insetInlineEnd: inset };
    return { ...vertical, ...horizontal };
}
/**
 * Resolve the chip for one product, or null.
 *
 * Returns null — no chip at all — when the seller has not switched badges on, when the server said
 * `NONE`, or when a deliberately blanked label leaves nothing to render. A blank label in the THEME
 * settings is not "no chip": it means "use the translated one", which is why the fallback is a `t()` call
 * and not an empty string.
 */
export function resolveUrgencyBadge(product, context) {
    const settings = context.themeSettings?.productBadges ?? {};
    const enabled = settings.enabled ?? DEFAULT_PRODUCT_BADGES.enabled;
    if (!enabled)
        return null;
    const urgency = product.urgency ?? 'NONE';
    if (urgency === 'NONE')
        return null;
    const soldOut = urgency === 'SOLD_OUT';
    const custom = (soldOut ? settings.soldOutText : settings.highDemandText) ?? '';
    // Her words win in every language; blank falls back to the translated label so an Arabic shop reads
    // correctly out of the box.
    const text = custom.trim() || t(context, soldOut ? 'product.sold_out_short' : 'product.high_demand');
    if (!text)
        return null;
    const background = (soldOut ? settings.soldOutBg : settings.highDemandBg) ??
        (soldOut ? DEFAULT_PRODUCT_BADGES.soldOutBg : DEFAULT_PRODUCT_BADGES.highDemandBg);
    const color = (soldOut ? settings.soldOutFg : settings.highDemandFg) ??
        (soldOut ? DEFAULT_PRODUCT_BADGES.soldOutFg : DEFAULT_PRODUCT_BADGES.highDemandFg);
    const radius = settings.radius ?? DEFAULT_PRODUCT_BADGES.radius;
    const position = settings.position ?? DEFAULT_PRODUCT_BADGES.position;
    return {
        text,
        style: {
            position: 'absolute',
            ...placement(position),
            // Above both media layers and the countdown chip's own layer, below the card's full-card link
            // overlay (z-index 1 in Shrine's `.card__heading a::after`) — the chip must never eat the click
            // that opens the product.
            zIndex: 2,
            pointerEvents: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: 'calc(100% - 24px)',
            boxSizing: 'border-box',
            padding: '4px 10px',
            borderRadius: `${radius}px`,
            background,
            color,
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: 1.4,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    };
}
/**
 * Render the chip for a product, or nothing. Drop this inside the card's RELATIVELY POSITIONED image
 * frame — every card in this codebase already has one (it is what the countdown chip is pinned to).
 */
export function UrgencyBadge({ product, context, }) {
    const badge = resolveUrgencyBadge(product, context);
    if (!badge)
        return null;
    return (_jsx("span", { className: "aqx-urgency-badge", "data-aqx-urgency": product.urgency, style: badge.style, children: badge.text }));
}
//# sourceMappingURL=UrgencyBadge.js.map